import {
  lookupPipeStatusByPipeId,
  type LookupPipeStatusByPipeIdResult,
} from '../../pipefitter/api/pipeStatusSearch';
import { fetchPipeStatusListUnpaginated } from '../../pipefitter/api/pipeStatusList';
import type { PipeStatusItem } from '../../pipefitter/types';

export type PipeInventoryFitterLookupInput = {
  projectNumber: string;
  projectId?: string | number | null;
  /** End id → Fitter pipeID */
  pipeNo: string;
  /** Size line → Fitter pipe_id (optional but preferred) */
  pipePipeId?: string | null;
  projectName?: string;
};

export type PipeInventoryFitterLookupResult =
  | { status: 'found'; pipe: PipeStatusItem }
  | { status: 'not_found' }
  | { status: 'error'; message: string };

function normalizeEndId(value: string): string {
  return value.trim();
}

function findRowByPipeId(rows: PipeStatusItem[], pipeNo: string): PipeStatusItem | null {
  const target = normalizeEndId(pipeNo).toLowerCase();
  if (!target) return null;
  const match = rows.find((row) => String(row.pipeID ?? '').trim().toLowerCase() === target);
  return match ?? null;
}

/**
 * Analysis-facing Fitter end lookup: prefer getPipeStatusByPipeId, fall back to list + client filter.
 * Does not read OD/wall from pipeData.
 */
export async function lookupPipeInventoryFitterEnd(
  input: PipeInventoryFitterLookupInput
): Promise<PipeInventoryFitterLookupResult> {
  const projectNumber = String(input.projectNumber ?? '').trim();
  const pipeNo = normalizeEndId(String(input.pipeNo ?? ''));
  const materialPipeId = String(input.pipePipeId ?? '').trim();

  if (!projectNumber || !pipeNo) {
    return { status: 'not_found' };
  }

  if (materialPipeId) {
    try {
      const byId: LookupPipeStatusByPipeIdResult = await lookupPipeStatusByPipeId({
        projectNumber,
        projectId: input.projectId,
        materialPipeId,
        pipeID: pipeNo,
        projectName: input.projectName,
      });
      if (byId.exists && byId.pipe) {
        return { status: 'found', pipe: byId.pipe };
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Pipe Fitter lookup failed';
      // Fall through to list filter before surfacing error.
      try {
        const rows = await fetchPipeStatusListUnpaginated({
          project_num: projectNumber,
          project_id: input.projectId,
          pipe_id: materialPipeId,
          project_name: input.projectName,
        });
        const match = findRowByPipeId(rows, pipeNo);
        if (match) return { status: 'found', pipe: match };
        return { status: 'error', message };
      } catch {
        return { status: 'error', message };
      }
    }
  }

  try {
    const rows = await fetchPipeStatusListUnpaginated({
      project_num: projectNumber,
      project_id: input.projectId,
      pipe_id: materialPipeId || undefined,
      project_name: input.projectName,
    });
    const match = findRowByPipeId(rows, pipeNo);
    if (match) return { status: 'found', pipe: match };
    return { status: 'not_found' };
  } catch (error: unknown) {
    return {
      status: 'error',
      message: error instanceof Error ? error.message : 'Pipe Fitter list lookup failed',
    };
  }
}

/** Edit must never auto-run Fitter prefill (PRD / issue 04). */
export function isPipeInventoryFitterPrefillAllowed(mode: 'add' | 'edit'): boolean {
  return mode === 'add';
}
